// src/components/shipment/ShipmentChargesCard.jsx
/**
 * Shipment charges card for the merchant shipment-details page.
 *
 * One card, one statement: rendered from GET /v3/oms/merchant/order/charges (useShipmentCharges).
 * The statement is the merchant-facing truth — the card never derives money from timeline codes,
 * never shows pricing components (freight / tax split), and never invents a ₹0.00 for a pending line.
 *
 * Anatomy follows the sibling tiles (label row → the one number → detail → footnote): the net cost
 * leads, the ledger lines explain it, the refund answer sits where the eye lands after the lines,
 * and the settlement footnote closes. AWB / order reference are not repeated — the hero card has them.
 *
 * States: loading skeleton (holds the number + two lines, honours reduced motion) → statement |
 * empty | unavailable (404, quiet) | error (with Retry).
 */
import React, { useCallback, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { Check, Copy, CreditCard, Wallet as WalletIcon, CircleCheck, CircleX, Clock } from "lucide-react";
import useShipmentCharges from "../../hooks/useShipmentCharges";
import { useToast } from "../toast/ToastProvider";

/* ------------------------------ formatting ------------------------------ */
/* Same dialect as the page's prettyMoney / fmtDayTime: unknown money is "—", never a fabricated
   ₹0.00; instants are shown in IST. Kept local so the card owns its rendering, like its siblings. */
const money = (n) =>
  n == null || n === "" || Number.isNaN(Number(n)) ? "—" : `₹${Number(n).toFixed(2)}`;

const istDayTime = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/* ------------------------------ statement vocabulary ------------------------------ */
const LINE_LABELS = {
  SHIPPING: "Shipping charge",
  REVERSE_PICKUP: "Reverse pickup charge",
  RTO_CHARGE: "RTO charge",
  COD_FEE_RETURNED: "COD fee returned",
  REFUND: "Refund",
};

const lineLabel = (item) => {
  if (item.reason === "WEIGHT_DISPUTE") {
    return item.direction === "CREDIT" ? "Weight dispute refund" : "Weight dispute charge";
  }
  if (LINE_LABELS[item.reason]) return LINE_LABELS[item.reason];
  // OTHER (and any reason newer than this card): the ledger's own wording — money is never hidden.
  return item.description || "Other adjustment";
};

/* Every amount on the statement is what actually moved — GST included; the merchant is never left to
   wonder whether tax is still to come. */
const SETTLEMENTS = {
  PREPAID_WALLET: { pill: "Prepaid Wallet", note: "Charged from your wallet. Amounts include GST." },
  POSTPAID: { pill: "Postpaid", note: "Billed to your postpaid account. Amounts include GST." },
  COD_NETOFF: {
    pill: "COD Netoff",
    note: "Adjusted against your COD remittance — you receive COD minus these charges. Amounts include GST.",
  },
};

const walletNoun = (settlementType) => {
  if (settlementType === "COD_NETOFF") return "your COD net-off wallet";
  if (settlementType === "POSTPAID") return "your postpaid account";
  return "your wallet";
};

const REFUND_TONES = {
  ok: { box: "bg-emerald-50", ink: "text-emerald-700", Icon: CircleCheck },
  wait: { box: "bg-amber-50", ink: "text-amber-700", Icon: Clock },
  none: { box: "bg-slate-100", ink: "text-slate-700", Icon: CircleX },
};

/* ------------------------------ pieces ------------------------------ */
/* Card chrome, label row and pill are the sibling tiles' (MetricTile), so the row reads as one set. */
const CARD_CLASS = "flex h-full flex-col rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm";

/* Wraps only when it must (≈320px): the pill drops under the title instead of overflowing the card. */
function CardHeader({ pill }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-[#3877FC]/10 text-[#2A59BD]">
        <CreditCard className="h-4 w-4" aria-hidden="true" />
      </span>
      <h3 className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-wider text-slate-400">
        Shipment Charges
      </h3>
      {pill ? (
        <span className="ml-auto whitespace-nowrap rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          {pill}
        </span>
      ) : null}
    </div>
  );
}

/** The transaction id in full, monospaced, with a copy button — what a merchant quotes to support. */
function TransactionId({ id, label = "Txn" }) {
  const { showToast } = useToast();
  const [copied, setCopied] = useState(false);
  const value = id == null ? "" : String(id);

  const onCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      showToast("Transaction ID copied", "success", 1500);
      setTimeout(() => setCopied(false), 1200);
    } catch (err) {
      showToast("Couldn't copy the transaction ID", "error", 2000);
    }
  }, [value, showToast]);

  if (!value) return null;
  return (
    <span className="inline-flex min-w-0 max-w-full items-center gap-1 align-middle">
      <span className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</span>
      <span className="min-w-0 break-all font-mono text-[11px] leading-4 text-slate-500">{value}</span>
      <button
        type="button"
        onClick={onCopy}
        aria-label={copied ? "Transaction ID copied" : "Copy transaction ID"}
        title={copied ? "Copied" : "Copy transaction ID"}
        className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC] focus-visible:ring-offset-1"
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" />
        ) : (
          <Copy className="h-3.5 w-3.5" aria-hidden="true" />
        )}
      </button>
    </span>
  );
}

function ChargeLine({ item }) {
  const credit = item.direction === "CREDIT";
  // A pending line with no amount yet shows the chip, not ₹0.00.
  const showPendingChip = item.pending && !(Number(item.amount) > 0);
  const when =
    item.pending && !item.transactionDateTimeUtc ? "Awaiting confirmation" : istDayTime(item.transactionDateTimeUtc);
  return (
    <li className="py-2.5 first:pt-0 last:pb-0">
      <div className="flex items-baseline justify-between gap-4">
        <span className="min-w-0 break-words text-sm font-medium text-slate-800">{lineLabel(item)}</span>
        {showPendingChip ? (
          <span className="flex-shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold tracking-wide text-amber-700">
            Pending
          </span>
        ) : (
          <span
            className={`flex-shrink-0 whitespace-nowrap text-sm tabular-nums ${
              credit ? "font-semibold text-emerald-700" : "font-medium text-slate-900"
            }`}
          >
            {credit ? "+" : ""}
            {money(item.amount)}
          </span>
        )}
      </div>
      {when ? <div className="mt-0.5 text-xs text-slate-500">{when}</div> : null}
      {item.transactionId ? (
        <div className="mt-0.5">
          <TransactionId id={item.transactionId} />
        </div>
      ) : null}
    </li>
  );
}

const refundCopy = (refund, settlementType) => {
  const wallet = walletNoun(settlementType);
  switch (refund.state) {
    case "REFUNDED": {
      const when = istDayTime(refund.refundedDateTimeUtc);
      return {
        tone: "ok",
        head: `Refunded ${money(refund.amount)}`,
        body: `Credited to ${wallet}${when ? ` on ${when}` : ""}.`,
        transactionId: refund.transactionId,
      };
    }
    case "PENDING_COURIER_CONFIRMATION":
      return {
        tone: "wait",
        head: "Cancellation requested",
        body: "Waiting for the courier to confirm. Your shipping charge will be refunded once they do.",
      };
    case "PENDING":
      return {
        tone: "wait",
        head: "Refund on its way",
        body: `Your shipping charge will be credited back to ${wallet}.`,
      };
    case "NOT_APPLICABLE":
      return {
        tone: "none",
        head: "No refund",
        body: "The shipment was picked up before it was cancelled, so the shipping charge stands.",
      };
    default:
      return null;
  }
};

function RefundBlock({ refund, settlementType }) {
  if (!refund) return null;
  const copy = refundCopy(refund, settlementType);
  if (!copy) return null;
  const { box, ink, Icon } = REFUND_TONES[copy.tone];
  return (
    <div className={`mt-3 flex items-start gap-2.5 rounded-lg px-3 py-2.5 ${box}`}>
      <Icon className={`mt-0.5 h-4 w-4 flex-shrink-0 ${ink}`} aria-hidden="true" />
      <div className="min-w-0">
        <div className={`text-[13px] font-semibold ${ink}`}>{copy.head}</div>
        <div className="mt-0.5 break-words text-xs text-slate-700">{copy.body}</div>
        {copy.transactionId ? (
          <div className="mt-1">
            <TransactionId id={copy.transactionId} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** The number the tile leads with, and the one-line caption under it. */
function Headline({ items, netCost }) {
  const hasConfirmed = items.some((item) => !item.pending);
  if (!items.length) {
    return <HeadlineValue value="—" caption="No charges yet" />;
  }
  if (!hasConfirmed) {
    return <HeadlineValue value="—" caption="Charge pending confirmation" />;
  }
  const credits = items.some((item) => !item.pending && item.direction === "CREDIT");
  return <HeadlineValue value={money(netCost)} caption={credits ? "Net cost, after credits" : "Net cost of this shipment"} />;
}

function HeadlineValue({ value, caption }) {
  return (
    <div className="mt-2.5">
      <div className="text-xl font-semibold leading-tight tracking-tight tabular-nums text-slate-900">{value}</div>
      <div className="mt-0.5 text-xs text-slate-500">{caption}</div>
    </div>
  );
}

/* Placeholder sized to the real metrics (number 28px + caption 16px; two lines of 20px + 16px + 16px)
   so the card does not jump when the statement lands. */
function StatementSkeleton({ reduce }) {
  const bar = (width, tone = "bg-slate-200") => <div className={`h-3 rounded ${tone} ${width}`} />;
  return (
    <div className={reduce ? "" : "animate-pulse"} aria-label="Loading shipment charges">
      <div className="mt-2.5 flex h-7 items-center">
        <div className="h-5 w-24 rounded bg-slate-200" />
      </div>
      <div className="mt-0.5 flex h-4 items-center">{bar("w-36", "bg-slate-100")}</div>
      <ul className="mt-3 divide-y divide-slate-100 border-t border-slate-100 pt-3">
        {[
          ["w-28", "w-32"],
          ["w-24", "w-40"],
        ].map(([labelWidth, metaWidth]) => (
          <li key={labelWidth} className="py-2.5 first:pt-0 last:pb-0">
            <div className="flex h-5 items-center justify-between gap-4">
              {bar(labelWidth)}
              {bar("w-14")}
            </div>
            <div className="mt-0.5 flex h-4 items-center">{bar(metaWidth, "bg-slate-100")}</div>
            <div className="mt-0.5 flex h-4 items-center">{bar("w-52", "bg-slate-100")}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ------------------------------ card ------------------------------ */
export default function ShipmentChargesCard({ orderReferenceNumber }) {
  const { loading, error, data, retry } = useShipmentCharges(orderReferenceNumber);
  const reduce = useReducedMotion();

  if (loading) {
    return (
      <section className={CARD_CLASS} aria-busy="true">
        <CardHeader />
        <StatementSkeleton reduce={reduce} />
      </section>
    );
  }

  if (error || !data) {
    return (
      <section className={CARD_CLASS}>
        <CardHeader />
        {!error || error.kind === "not-found" ? (
          <p className="mt-3 text-[13px] text-slate-500">Charges aren't available for this shipment.</p>
        ) : (
          <div
            role="alert"
            className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-rose-50 px-3.5 py-3 text-[13px] text-rose-700"
          >
            <span>Couldn't load the charges for this shipment.</span>
            <button
              type="button"
              onClick={retry}
              className="flex-shrink-0 rounded-md border border-current px-2.5 py-1 text-xs font-semibold transition-colors hover:bg-rose-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC] focus-visible:ring-offset-2 focus-visible:ring-offset-rose-50"
            >
              Retry
            </button>
          </div>
        )}
      </section>
    );
  }

  const settlement = SETTLEMENTS[data.settlementType] || null;
  const { items } = data;

  return (
    <section className={CARD_CLASS}>
      <CardHeader pill={settlement?.pill} />
      <Headline items={items} netCost={data.netCost} />

      {items.length ? (
        <ul className="mt-3 divide-y divide-slate-100 border-t border-slate-100 pt-3">
          {items.map((item, i) => (
            <ChargeLine key={`${item.reason}-${item.transactionId || "pending"}-${i}`} item={item} />
          ))}
        </ul>
      ) : (
        <div className="mt-3 rounded-lg border border-dashed border-slate-200 px-3.5 py-3.5 text-[13px] text-slate-600">
          Charges appear here once the shipment is booked.
        </div>
      )}

      <RefundBlock refund={data.refund} settlementType={data.settlementType} />

      <div className="mt-auto pt-3">
        <div className="flex items-start gap-1.5 border-t border-slate-100 pt-2.5 text-xs leading-relaxed text-slate-500">
          <WalletIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-slate-400" aria-hidden="true" />
          <span>{settlement ? settlement.note : "Amounts include GST."}</span>
        </div>
      </div>
    </section>
  );
}
